import { useMemo, useState, type FormEvent } from "react";

import { RouteMap } from "@/components/RouteMap";
import { SCENARIO_CONFIGS, type ScenarioLocation } from "@/lib/ai-mocks";

const LOCATION_SUMMARY: Record<ScenarioLocation, { start: string; event: string; finish: string }> = {
  coastal: { start: "Waypoint Alpha", event: "Checkpoint sweep", finish: "Silent extraction" },
  urban: { start: "Rooftop insertion", event: "Narrow corridor scan", finish: "Shielded handoff" },
  forest: { start: "Canopy breach point", event: "Thermal sweep", finish: "Creekside exfil" },
};

const DEFAULT_PROMPT =
  "Low-altitude reconnaissance with one blocked corridor, two surveillance checkpoints, and a final silent extraction zone.";

export function AiScenariosPage() {
  const [location, setLocation] = useState<ScenarioLocation>("coastal");
  const [prompt, setPrompt] = useState(DEFAULT_PROMPT);
  const [status, setStatus] = useState(
    "Waiting for a prompt. This generator is mocked.",
  );
  const [copy, setCopy] = useState(
    "Mocked route with one blocked corridor, two checkpoints, and a silent extraction finish.",
  );
  const [loading, setLoading] = useState(false);
  const [runStatus, setRunStatus] = useState(
    "Mock action only. No scenario export exists yet.",
  );
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(LOCATION_SUMMARY.coastal);

  const scenario = SCENARIO_CONFIGS[location];
  const routes = useMemo(
    () => [{ path: scenario.points.map((p) => [p.latitude, p.longitude]), color: scenario.color, points: scenario.points }],
    [scenario],
  );

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLoading(true);
    setStatus(`Generating mocked scenario for ${location}...`);
    window.setTimeout(() => {
      setLoading(false);
      setStatus("Mock scenario generated. No real AI processing occurred.");
      setCopy(
        `Mock output for "${prompt.trim() || "Prototype reconnaissance mission"}" in the ${location} environment.`,
      );
      setSummary(LOCATION_SUMMARY[location]);
    }, 1300);
  }

  function runVbs() {
    setBusy(true);
    setRunStatus("Mock VBS launch in progress...");
    window.setTimeout(() => {
      setBusy(false);
      setRunStatus("Mock complete. No real VBS process was started.");
    }, 1500);
  }

  return (
    <div className="mock-page" id="ai-scenarios-page">
      <section className="mock-hero">
        <div className="mock-hero-copy">
          <div className="mock-badges">
            <span className="mock-badge mock-badge-hot">MOCKUP</span>
            <span className="mock-badge">SIMULATED SCENARIO GENERATION</span>
          </div>
          <p className="eyebrow">Scenario ideation</p>
          <h1>AI Scenarios</h1>
          <p className="mock-lede">
            Mocked 2D map generator where the user selects a location, writes a prompt, waits for a fake load,
            and gets an animated prototype scenario drawn directly on the map.
          </p>
        </div>
        <div className="mock-hero-actions">
          <button
            className="button primary mock-run-button"
            type="button"
            onClick={runVbs}
            disabled={busy}
          >
            {busy ? "Run VBS (mock loading...)" : "Run VBS"}
          </button>
          <span className="mock-run-status">{runStatus}</span>
        </div>
      </section>

      <section className="mock-warning-strip">
        <strong>Mock generation only.</strong>
        <span>The prompt parser, location model, and drawn route are synthetic placeholders.</span>
      </section>

      <section className="mock-grid mock-grid-scenarios">
        <article className="panel mock-panel mock-watermarked">
          <div className="panel-head">
            <h2>Scenario generator</h2>
            <p>Choose a mocked location and describe the desired exercise</p>
          </div>
          <form className="mock-form" onSubmit={handleSubmit}>
            <div className="mock-chip-group">
              {(Object.keys(SCENARIO_CONFIGS) as ScenarioLocation[]).map((key) => (
                <button
                  key={key}
                  className={`mock-chip${location === key ? " is-active" : ""}`}
                  type="button"
                  onClick={() => setLocation(key)}
                >
                  {key === "coastal" && "Coastal range"}
                  {key === "urban" && "Dense urban"}
                  {key === "forest" && "Forest corridor"}
                </button>
              ))}
            </div>
            <label className="perf-filter">
              <span>Scenario brief</span>
              <textarea
                className="mock-textarea"
                rows={5}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
              />
            </label>
            <div className="mock-form-actions">
              <button className="button primary" type="submit" disabled={loading}>
                Generate mock scenario
              </button>
              <span className="mock-loading-indicator">{status}</span>
            </div>
          </form>
        </article>

        <article className="mock-panel mock-watermarked">
          <div className="mock-surface-wrap mock-scenario-stage">
            {loading && (
              <div className="mock-loading-overlay is-visible">
                <div className="mock-spinner" />
                <span>Generating mocked scenario...</span>
              </div>
            )}
            <article className="surface-panel replay-2d-panel">
              <header>
                <h3>2D trajectory</h3>
                <p>Mocked Leaflet scenario drawn from the selected location.</p>
              </header>
              <div className="map-surface compare-map-surface">
                <RouteMap
                  routes={routes}
                  structures={scenario.structures}
                  homePoint={scenario.homePoint}
                  height={380}
                />
              </div>
            </article>
            <div className="mock-planner-caption">
              <strong>{scenario.title}</strong>
              <span>{copy}</span>
            </div>
          </div>
        </article>
      </section>

      <section className="panel mock-panel mock-watermarked">
        <div className="panel-head">
          <h2>Generated mock scenario summary</h2>
          <p>Short fake output based on the prompt above</p>
        </div>
        <div className="mock-scenario-summary">
          <article className="mock-summary-card">
            <span>Start zone</span>
            <strong>{summary.start}</strong>
            <small>Prompt-informed mocked start zone.</small>
          </article>
          <article className="mock-summary-card">
            <span>Key event</span>
            <strong>{summary.event}</strong>
            <small>
              Derived from: "{prompt.slice(0, 56)}
              {prompt.length > 56 ? "..." : ""}"
            </small>
          </article>
          <article className="mock-summary-card">
            <span>Finish zone</span>
            <strong>{summary.finish}</strong>
            <small>Prototype end state for UI review only.</small>
          </article>
        </div>
      </section>
    </div>
  );
}
